<template>
  <div class="search-resource-detail">
    <inline-loader v-if="isLoading" />
    <div v-else class="form-group">
      <cv-form class="meta">
        <!-- read only indicator -->
        <dl v-if="showInlineNotification" class="field-set type-text read-only-indicator">
          <dd class="field-value">
            <svg-icon name="PencilStrike24" kind="indicative" />
            <span>{{ readOnlyIndicator }}</span>
          </dd>
        </dl>

        <!-- List of activities -->
        <div v-if="showActivityList" class="activity-list">
          <cv-inline-notification
            v-if="showEmptyLessonBanner"
            :title="$t('search_resource_detail_empty_lesson_info_banner')"
            low-contrast
            class="banner"
            kind="info"
            hide-close-button
          />
          <data-table-list v-else class="activity-list-datatable" in-subpage>
            <template #selection-bar>
              <selection-bar :count="rowsSelected" @clear="handleClearSelection" />
            </template>
            <!--FIXME VUE3: CURRENTLY NOT SUPPORTED
              mobile-breakpoint="0"
            -->
            <v-data-table
              :headers="getDatatableHeaders()"
              :items="getDatatableItems()"
              :items-per-page="20"
              fixed-header
              item-value="id"
              mobile-breakpoint="0"
            >
              <template
                v-if="!params.hasSelectAction && !fromLessonTemplate"
                #[`headers`]
              ></template>
              <template #[`header.title`]="{ column }">
                <div v-if="params.hasSelectAction" class="all-checkbox" @click.stop>
                  <input-checkbox
                    id="student-select-all-checkbox"
                    value="select-all"
                    :selected-values="selectAllActivities"
                    :mixed="showMixedCheckbox"
                    @change="handleSelectAll"
                  />
                </div>
                <span>{{ column.title }}</span>
              </template>
              <template #[`item.title`]="{ item }">
                <!-- "simple-view" will be added if canViewInNewTab is false when accessed by a Student,
                  -  as student shouldn't see the ALP label and notes by Teacher
                  -->
                <data-table-cell
                  class="title"
                  :class="{
                    'simple-view': !params.canViewInNewTab,
                    'has-select-action': params.hasSelectAction,
                  }"
                >
                  <div class="activity-header-wrapper">
                    <div v-if="params.hasSelectAction" class="activity-checkbox" @click.stop>
                      <input-checkbox
                        :id="item.id.toString()"
                        :value="item.id.toString()"
                        :selected-values="selectedActivities"
                        @change="handleSelect"
                      />
                    </div>
                    <span class="icon">
                      <svg-icon
                        :name="getActivityIconNameByTypeAndOptional(item.type, item.optional)"
                        kind="indicative"
                      />
                      <span v-if="!hideSectionActivityNumber" class="page-label">
                        {{ item.pageNo }}
                      </span>
                    </span>
                    <div class="page-title-group">
                      <span class="page-title">{{ item.title }}</span>
                      <label-component
                        v-if="params.showMoreInformation && !isCustomTag(item)"
                        kind="alp-stage"
                        :class="item.alpStage"
                        :text="item.alpStageDisplayName"
                      />
                    </div>
                    <svg-icon
                      v-if="params.canViewInNewTab"
                      class="view-ext"
                      name="ExternalLink24"
                      kind="action"
                      @click="handleOpenActivityInNewTab(item.id)"
                    />
                  </div>
                  <rich-text
                    v-if="params.showMoreInformation && showNotes(item.notes)"
                    class="notes"
                    :content="item.notes"
                  />
                </data-table-cell>
              </template>
              <template #bottom></template>
            </v-data-table>
          </data-table-list>
        </div>

        <div class="field-group">
          <!-- Question Parts -->
          <dl v-if="showQuestionParts" class="field-set type-text question-parts">
            <dt class="field-label">{{ $t("search_resource_detail_question_parts_label") }}</dt>
            <dd class="field-input">
              <search-resource-detail-question-parts
                :mpq-question="params.mpqQuestion"
                :current-question="params.question"
                :selectable="params.hasSelectAction"
                :add-from-mpq-child-layer="params.addFromMpqChildLayer"
                :add-from-mpq-sub-layer="params.addFromMpqSubLayer"
                @change-question-preview="handleCurrentQuestionChanged"
                @select="handleSelect"
              />
            </dd>
          </dl>
        </div>

        <div class="field-group">
          <!-- Location -->
          <dl v-if="showLocation" class="field-set type-text location">
            <dt class="field-label">{{ $t("search_resource_detail_location_label") }}</dt>
            <dd class="field-value">
              <span class="message">
                {{ params.location }}
              </span>
            </dd>
          </dl>
        </div>
      </cv-form>

      <cv-form class="other-information">
        <h5 class="form-header">{{ $t("search_resource_detail_other_information_label") }}</h5>
        <!-- question-type -->
        <dl v-if="isQuestionType" class="field-set type-text question-type">
          <dt class="field-label">{{ $t("search_resource_detail_question_label") }}</dt>
          <dd class="field-value">
            <span class="message">
              {{ questionType }}
            </span>
          </dd>
        </dl>
        <!-- generalized feedback assistant for lfa, se, ak -->
        <dl v-if="showFeedbackAssistant" class="field-set type-text">
          <dt class="field-label">
            <span>{{ feedbackAssistantLabel }} </span>
          </dt>
          <dd class="field-value">{{ feedbackAssistantAppName }}</dd>
        </dl>

        <!-- Content Tags -->
        <dl v-if="showResourceTaggings" class="field-set type-text content-tag">
          <dt class="field-label">{{ $t("search_resource_detail_tag_label") }}</dt>
          <dd class="field-value">
            <resource-taggings-view
              :resource-type="params.type"
              :resource-uuid="params.uuid"
              single-empty-indicator="true"
            />
          </dd>
        </dl>
        <!-- contributors -->
        <dl v-if="showContributors" class="field-set type-text contributors">
          <dt class="field-label">{{ $t("search_resource_detail_credited_label") }}</dt>
          <dd class="field-value">
            <div class="output-text">
              <label-component
                kind="creditor"
                :class="{ 'creditor-moe': !canOpenProfile }"
                :text="contributors"
                @click="handleProfileClick(contributorsId, isMOEResource)"
              />
            </div>
            <div v-if="showTruncate(1) && showContributors" class="truncate" />
            <time class="date">{{ createdDate }}</time>
          </dd>
        </dl>
        <!-- modified by -->
        <dl v-if="showModifiedBy" class="field-set type-text modified-by">
          <dt class="field-label">{{ $t("search_resource_detail_edited_label") }}</dt>
          <dd class="field-value">
            <label-component
              kind="creditor"
              :text="modifiedBy"
              @click="handleProfileClick(modifiedById, false)"
            />
            <time class="date">{{ modifiedDate }}</time>
          </dd>
        </dl>
        <!-- lesson keywords -->
        <match-indicator v-if="matchKeyword" />
        <dl v-if="showKeywordTagsField" class="field-set type-text keywords">
          <dt class="field-label">{{ $t("search_resource_detail_keywords_label") }}</dt>
          <dd class="field-value">
            <div v-if="showKeywordsTags" class="output-text">
              <keywords :tags="keywords" />
            </div>
            <div v-else class="output-text">
              {{ keywords }}
            </div>
            <div v-if="showTruncate(0)" class="truncate" />
          </dd>
        </dl>
      </cv-form>

      <cv-form class="linked-resources">
        <div v-if="showUsedIn" class="field-set type-text used-in">
          <AccordionWrapper
            kind="ghost"
            class="accordion-component"
            @state-change="accordionActionChange"
          >
            <cv-accordion-item key="usedIn" :open="false">
              <template #title>{{ usedInLabel }}</template>
              <template #content>
                <div v-if="hasContextData" class="lesson-card-wrapper">
                  <lesson-detail-card
                    v-for="data in contextData"
                    :key="data.uuid"
                    :context-data="data"
                    :search-filters="params.searchFilters"
                  />
                </div>
                <span v-else class="message">-</span>
              </template>
            </cv-accordion-item>
          </AccordionWrapper>
        </div>
      </cv-form>
    </div>
  </div>
</template>

<script lang="ts">
import SettingsSummaryMixin from "@modules/editor/mixin/settings.summary.mixin";
import Keywords from "@plugins/keywords/components/Keywords";
import DateTimeFormatter from "@utils/datetime.formatter";
import LessonDetailCard from "@plugins/common/components/LessonDetailCard";
import resourceApi from "@apis/resource.api";
import mediaObjectApi from "@apis/media.object.api";
import { isDefined, isEmpty, notNull } from "@utils/var.check";
import ResourceTaggingsView from "@modules/editor/components/resource-taggings/ResourceTaggingsView";
import { LOCATION_TYPE, RESOURCE_DOMAIN } from "@modules/search/service/search.constant";
import { RESOURCE_DOMAIN_PATH } from "@plugins/common/constant/resource.constant";
import DateTimeUtils from "@utils/datetime.utils";
import ActivityIconMixin from "@plugins/common/mixin/activity.icon.mixin";
import { mapGetters } from "vuex";
import { NAMESPACE, QUESTION_TYPE } from "@modules/attempt/service/attempt.constant";
import { APP_USAGE_TYPE, INTEGRATOR_APP_FEATURE_CATEGORY } from "@modules/app/service/app.constant";
import sortUtils from "@utils/sort.utils";
import SearchResourceDetailQuestionParts from "@modules/search/components/SearchResourceDetailQuestionParts";
import RoutingMixin from "@plugins/common/mixin/routing.mixin";
import SearchProfile from "@modules/search/mixin/search.profile.mixin.ts";
import { RESOURCE_TYPE } from "@/modules/editor/service/editor.constant";
import searchStore from "@modules/search/components/SearchStore";

export default {
  name: "SearchResourceDetail",
  components: {
    LessonDetailCard,
    Keywords,
    ResourceTaggingsView,
    SearchResourceDetailQuestionParts,
  },
  mixins: [SettingsSummaryMixin, ActivityIconMixin, RoutingMixin, SearchProfile],
  props: { params: { type: Object, default: null }, context: { type: String, required: true } },
  emits: ["activitiesSelected", "currentQuestionChanged"],
  data() {
    return {
      searchStore: searchStore,
      activityTitle: `Resource`,
      visible: true,
      hasPlaceholder: true,
      isLoading: true,
      contextData: {},
      readOnlyIndicator: this.$i18n.t("search_resource_detail_notification_subtitle", {
        resource: this.params.type.charAt(0) + this.params.type.slice(1).toLowerCase(),
      }),
      showInlineNotification: this.params.type === "QUESTION" || this.params.type === "QUIZ",
      selectAllActivities: [],
      selectedActivities: [],
    };
  },
  computed: {
    ...mapGetters(NAMESPACE.APP, [
      "getIntegratorAppAccessByAppFeatureId",
      "getIntegratorAppAccessByCategory",
    ]),
    isLessonType() {
      return this.params.type === "LESSON" || this.params.type === "ASSIGNMENT_LESSON";
    },
    isMediaType() {
      return this.params.type === "MEDIA";
    },
    isQuestionType() {
      return this.params.type === "QUESTION";
    },
    isQuizType() {
      return this.params.type === "QUIZ";
    },
    lesson() {
      return this.params.lesson;
    },
    question() {
      return this.params.question;
    },
    mediaMetadata() {
      return this.params.mediaObject;
    },
    copyrightData() {
      return this.params.copyright;
    },
    quiz() {
      return this.params.quiz;
    },
    isCustomTag() {
      return (item) => {
        return item.alpStage === "CUSTOM";
      };
    },
    showNotes() {
      return (notes) => {
        return !isEmpty(notes);
      };
    },
    showModifiedBy() {
      if (this.isMediaType) {
        return !isEmpty(this.mediaMetadata.modifiedByName);
      } else if (this.isQuestionType) {
        return !isEmpty(this.question.modifiedBy);
      } else if (this.isQuizType) {
        return !isEmpty(this.quiz.modifiedByName);
      } else if (this.isLessonType) {
        return !isEmpty(this.lesson.modifiedByName);
      } else {
        return false;
      }
    },
    getResource() {
      if (this.isMediaType) {
        return this.mediaMetadata;
      } else if (this.isQuestionType) {
        return this.question;
      } else if (this.isQuizType) {
        return this.quiz;
      } else if (this.isLessonType) {
        return this.lesson;
      } else {
        return {};
      }
    },
    title() {
      return this.getResource.title;
    },
    description() {
      return this.getResource.description;
    },
    contributors() {
      if (
        this.getResource.resourceDomain === LOCATION_TYPE[2].value ||
        this.getResource.publisher === LOCATION_TYPE[2].value
      ) {
        return `${this.$i18n.t("search_resource_detail_publisher_label")} ${this.getResource.ownerGroupString}`;
      } else {
        const originator = this.getResource.originator;
        const originatorName = this.isLessonType && this.getResource.originatorName;
        if (isDefined(originator)) {
          return originator.name;
        } else if (originatorName) {
          return originatorName;
        }
        return "-";
      }
    },
    contributorsId() {
      if (
        this.getResource.resourceDomain === LOCATION_TYPE[2].value ||
        this.getResource.publisher === LOCATION_TYPE[2].value
      ) {
        return 0;
      } else {
        const originator = this.getResource.originator;
        const originatorId = this.isLessonType && this.getResource.originatorId;
        if (isDefined(originator)) {
          return originator.id;
        } else if (originatorId) {
          return originatorId;
        }
        return 0;
      }
    },
    canOpenProfile() {
      return !this.isMOEResource || this.contributorsId !== 0;
    },
    activityList() {
      return this.lesson.lessonActivityList;
    },
    showActivityList() {
      return this.isLessonType && notNull(this.activityList);
    },
    isAdminView() {
      return this.params.admin && this.params.admin === "admin";
    },
    showLocation() {
      if (this.isMediaType) {
        return !this.isAdminView;
      }
      return true;
    },
    showUsedIn() {
      return !this.isLessonType;
    },
    showContributors() {
      return this.isQuizType || this.isQuestionType || this.isLessonType;
    },
    showViewCount() {
      return (
        this.isLessonType &&
        this.lesson.resourceDomain !== RESOURCE_DOMAIN.PERSONAL &&
        this.lesson.resourceDomain !== RESOURCE_DOMAIN.ALP_TEMPLATE &&
        this.lesson.resourceDomain !== RESOURCE_DOMAIN.ASSIGNMENT
      );
    },
    viewCount() {
      return this.lesson && !isEmpty(this.lesson.viewCount) ? this.lesson.viewCount : 0;
    },
    fromLessonTemplate() {
      return this.params.location && this.params.location === "Templates";
    },
    keywords() {
      return this.getResource.keywords ? this.getResource.keywords : "-";
    },
    showResourceTaggings() {
      return this.isQuestionType && !this.isMpqQuestion;
    },
    createdDate() {
      return DateTimeFormatter.formatDateTimeToDateTimeAmPm(this.getResource.createdDate);
    },
    modifiedDate() {
      return DateTimeFormatter.formatDateTimeToDateTimeAmPm(this.getResource.modifiedDate);
    },
    getDate() {
      return (date) => {
        return date ? DateTimeFormatter.formatLocalDateTimeToDateOnly(date) : "-";
      };
    },
    modifiedBy() {
      if (this.isMediaType) {
        return this.mediaMetadata.modifiedByName;
      } else if (this.isQuestionType) {
        return this.question.modifiedBy.name;
      } else if (this.isQuizType) {
        return this.quiz.modifiedByName;
      } else if (this.isLessonType) {
        return this.lesson.modifiedByName;
      } else {
        return "";
      }
    },
    modifiedById() {
      if (this.isMediaType) {
        return this.mediaMetadata.modifiedById;
      } else if (this.isQuestionType) {
        return this.question.modifiedBy.id;
      } else if (this.isQuizType) {
        return this.quiz.modifiedById;
      } else if (this.isLessonType) {
        return this.lesson.modifiedById;
      } else {
        return "";
      }
    },
    createdBy() {
      return this.getResource.createdByName;
    },
    owner() {
      return this.copyrightData ? this.copyrightData.ownerUI : "-";
    },
    modification() {
      if (!this.copyrightData) {
        return "-";
      }
      return this.copyrightData.singleUseLicense
        ? this.$i18n.t("no_label")
        : this.getBooleanDisplay(this.copyrightData.modification);
    },
    getBooleanDisplay() {
      return (value) => {
        return value ? this.$i18n.t("yes_label") : this.$i18n.t("no_label");
      };
    },
    getIPSN() {
      return this.copyrightData.filingReference ? this.copyrightData.filingReference : "-";
    },
    showKeywordTagsField() {
      return !this.isLessonType && !this.isMpqQuestion;
    },
    showKeywordsTags() {
      return this.getResource.keywords && this.getResource.keywords.length > 0;
    },
    hasContextData() {
      return !isEmpty(this.contextData);
    },
    showSpeechEvaluationField() {
      return !isEmpty(this.question) && this.question.questionType === QUESTION_TYPE.REC;
    },
    isSpeechEvaluationQuestion() {
      return !isEmpty(this.question.speechEvaluation);
    },
    speechEvaluationName() {
      if (this.isSpeechEvaluationQuestion) {
        // to be changed in the future when there are more than 1 SE app type
        const appFeatureList = this.getIntegratorAppAccessByCategory(
          INTEGRATOR_APP_FEATURE_CATEGORY.SE,
        );
        let markAppFeatureId = 0;
        const questionApps = this.question.questionAppSettingsModels;
        if (!isEmpty(questionApps)) {
          questionApps.forEach((appFeature) => {
            if (appFeature.appUsage === APP_USAGE_TYPE.FEEDBACK_AND_MARKING) {
              markAppFeatureId = appFeature.appFeatureId;
            }
          });
        }
        const questionAppFeature = appFeatureList.find(
          (appFeature) =>
            appFeature.appFeatureId === markAppFeatureId &&
            appFeature.language === this.question.speechEvaluation,
        );
        return !isEmpty(questionAppFeature) ? questionAppFeature.appFeatureName : "-";
      }
      return "-";
    },
    showLFAField() {
      return !isEmpty(this.question) && this.question.questionType === QUESTION_TYPE.FREE;
    },
    isLFAQuestion() {
      return !isEmpty(this.question.appSettings);
    },
    lfaAppName() {
      if (this.isLFAQuestion) {
        const appFeature = this.getIntegratorAppAccessByAppFeatureId(
          this.question.appSettings[0].appFeatureId,
        );
        return appFeature.appFeatureName;
      }
      return "-";
    },
    questionType() {
      switch (this.question.questionType) {
        case "MCQ":
          return this.$i18n.t("search_filter_mcq_label");
        case "MRQ":
          return this.$i18n.t("search_filter_mrq_label");
        case "DND":
          return this.$i18n.t("search_filter_cnd_label");
        case "FILL":
          return this.$i18n.t("search_filter_fitb_label");
        case "FILE":
          return this.$i18n.t("search_filter_fsq_label");
        case "EEQ":
          return this.$i18n.t("search_filter_eeq_label");
        case "FREE":
          return this.$i18n.t("search_filter_frq_label");
        case "REC":
          return this.$i18n.t("search_filter_arq_label");
        case "MPQ":
          return this.$i18n.t("search_filter_mpq_label");
        default:
          return "-";
      }
    },
    fileSize() {
      return this.mediaMetadata.fileSizeFriendly;
    },
    rowsSelected() {
      return this.selectedActivities.length;
    },
    showMixedCheckbox() {
      return (
        this.selectedActivities.length > 0 &&
        this.selectedActivities.length < this.activityList.length
      );
    },
    showEmptyLessonBanner() {
      return !this.params.hasSelectAction && this.activityList.length === 0;
    },
    showFeedbackAssistant() {
      if (this.showLFAField) return true;
      if (this.showSpeechEvaluationField) return true;
      return false;
    },
    feedbackAssistantLabel() {
      return this.$i18n.t("search_resource_detail_question_lfa_label");
    },
    feedbackAssistantAppName() {
      if (this.showLFAField) return this.lfaAppName;
      if (this.showSpeechEvaluationField) return this.speechEvaluationName;
      return "";
    },
    hideSectionActivityNumber() {
      return this.params.lesson.hideSectionActivityNumber;
    },
    usedInLabel() {
      const length = this.hasContextData ? this.contextData.length : 0;
      return `${this.$i18n.t("search_resource_detail_used_in_label")} (${length})`;
    },
    showQuestionParts() {
      return this.isQuestionType && (this.isMpqQuestion || this.isMpqChildQuestion);
    },
    isMpqQuestion() {
      return this.isQuestionType && this.params.question.questionType === QUESTION_TYPE.MPQ;
    },
    isMpqChildQuestion() {
      return (
        this.isQuestionType &&
        this.params.question.topParentQuestionUuid !== null &&
        this.params.question.topParentQuestionUuid !== this.params.question.uuid
      );
    },
    matchKeyword() {
      return isEmpty(this.params.highlights) ? null : this.params.highlights.keyword;
    },
    isMOEResource() {
      return this.getResource.resourceDomain === RESOURCE_DOMAIN.MOE;
    },
  },
  watch: {
    selectedActivities: {
      handler() {
        this.$emit("activitiesSelected", this.selectedActivities);
      },
      deep: true,
    },
  },
  created() {
    if (this.isLessonType || this.isAdminView) {
      this.isLoading = false;
      return;
    }

    let uuid;
    // for MPQ, take top parent question uuid to fetch context
    if (this.params.type === "QUESTION" && this.params.question.topParentQuestionUuid !== null) {
      uuid = this.params.question.topParentQuestionUuid;
    } else {
      uuid = this.params.uuid;
    }

    const data = {
      uuid: uuid,
      type: this.params.type === "MEDIA" ? "MEDIAOBJECT" : this.params.type,
      raw: true, // get full context info
    };
    this.isLoading = true;
    resourceApi.fetchResourceContext(data).then((response) => {
      //OK
      if (response) {
        this.isLoading = false;
        this.contextData = response.data;
      }
    }, this.passiveApiFailHandler); //OK
  },
  methods: {
    accordionActionChange(data) {
      const accordionIndex = data.changedIndex;
      const accordionState = data.state[accordionIndex];

      // Since only have one accordion item ("usedIn"), can use a fixed ID
      const accordionItemId = "usedIn";
      const componentId = "search-resource-detail";

      if (this.accordionStore) {
        this.accordionStore.setAccordionItemState(componentId, accordionItemId, accordionState);
      }
    },
    calculateDuration(start, end) {
      if (!start || !end) {
        this.copyrightData.duration = { years: 0, months: 0, days: 0 };
        return;
      }
      const formartStartDate = DateTimeUtils.parseLocalDateTime(start);
      const formartEndDate = DateTimeUtils.parseLocalDateTime(end);

      //Get the year of this year
      let thisFullYear = formartEndDate.getFullYear();
      //Check how many days there where in last month
      const daysInLastMonth = new Date(thisFullYear, formartEndDate.getMonth(), 0).getDate();
      //Get this month
      let thisMonth = formartEndDate.getMonth();
      //Subtract to get difference between years
      thisFullYear = thisFullYear - formartStartDate.getFullYear();
      //Subtract to get difference between months
      thisMonth = thisMonth - formartStartDate.getMonth();
      //If month is less than 0 it means that we are some months before the start date in the year.
      // So we subtract one year, and add the negative number (month) to 12. (12 + -1 = 11)
      const thisDay = formartEndDate.getDate();
      thisMonth = thisMonth - 1;
      if (thisMonth < 0) {
        thisFullYear = thisFullYear - 1;
        thisMonth = 12 + thisMonth;
        //Get ends day of the month
      }
      //Subtract the start date from the number of days in the last month, add the result to todays day of the month
      //From 1-Aug-2016 to 1-Sep-2016 is to be considered as 1 month and 1 day
      let subAddDays = daysInLastMonth - formartStartDate.getDate() + 1;
      subAddDays = thisDay + subAddDays;

      if (subAddDays >= daysInLastMonth) {
        subAddDays = subAddDays - daysInLastMonth;
        thisMonth++;
        if (thisMonth > 11) {
          thisFullYear++;
          thisMonth = 0;
        }
      }

      this.copyrightData.duration = { years: thisFullYear, months: thisMonth, days: subAddDays };
    },
    handleDownload() {
      const uuid = this.params.uuid;
      this.handleRouteIntervalCheck();
      mediaObjectApi.downloadMediaObject({ uuid: uuid }).then((response) => {
        //OK
        if (response.code === "ok") {
          this.target = "DOWNLOAD_MEDIA_OBJECT";
          this.urlReady = response.data;
        } else {
          //todo: error message
        }
      }, this.passiveApiFailHandler); //OK
    },
    getDatatableHeaders() {
      return [
        {
          title: this.$t("search_lesson_preview_datatable_header_activity"),
          key: "title",
          sortable: false,
        },
      ];
    },
    getDatatableItems() {
      const sortConfig = [{ field: "pageNo", asc: true }];
      const mappedActivityList = this.activityList.map((activity) => {
        return {
          id: activity.id,
          title: activity.title,
          pageNo: activity.pageNo,
          type: activity.type,
          optional: activity.optional,
          alpStage: activity.alpStage.name,
          notes: activity.notes,
          alpStageDisplayName: activity.alpStage.displayName,
        };
      });
      return sortUtils.sortObjects(mappedActivityList, sortConfig);
    },
    handleSelect(value) {
      if (this.params.type === RESOURCE_TYPE.QUESTION) {
        this.searchStore.clearQuestionPartsSelectionList();
        value.forEach((questionUuid) => {
          this.searchStore.setQuestionPartsSelectionList({
            uuid: questionUuid,
          });
        });
        return;
      }

      this.selectedActivities = value;
      if (this.selectedActivities.length === this.activityList.length) {
        this.selectAllActivities = ["select-all"];
      } else {
        this.selectAllActivities = [];
      }
    },
    handleSelectAll(checked) {
      this.selectedActivities = [];
      if (checked.length) {
        this.selectedActivities = this.activityList.map((activity) => {
          return `${activity.id}`;
        });
      } else {
        this.selectAllActivities = [];
      }
    },
    handleClearSelection() {
      this.selectAllActivities = [];
      this.selectedActivities = [];
    },
    handleOpenActivityInNewTab(pageId) {
      let resourceUuid = this.params.lesson.uuid;
      let resourceDomain = "";
      switch (this.params.lesson.resourceDomain) {
        case "PERSONAL":
          resourceDomain = RESOURCE_DOMAIN_PATH.PERSONAL;
          break;
        case "MOE":
          resourceDomain = RESOURCE_DOMAIN_PATH.MOE_LIBRARY;
          break;
        case RESOURCE_DOMAIN.COMMUNITY:
        case "Community":
          resourceDomain = RESOURCE_DOMAIN_PATH.COMMUNITY_GALLERY;
          resourceUuid = this.params.communityResourceUuid;
          break;
        case "ALP_TEMPLATE":
          resourceDomain = RESOURCE_DOMAIN_PATH.ALP_TEMPLATE;
          resourceUuid = this.params.templateUuid;
          break;
        case "ASSIGNMENT":
          resourceDomain = RESOURCE_DOMAIN_PATH.CLASS_GROUP_RESOURCE;
          resourceUuid = this.params.lesson.uuid;
          break;
      }
      // TODO: SLS-11043, editor, add section id (seems like no usage, check SearchResourceDetailContentSubpage: search-course-resource-detail)
      const routeParams = {
        resourceDomain: resourceDomain,
        lessonUuid: resourceUuid,
        mode: "view",
        pageId: pageId,
      };
      if (this.isAdminView) {
        routeParams.admin = "admin";
      }

      const routeData = this.$router.resolve({ name: "EditorActivityPage", params: routeParams });
      window.open(routeData.href, "_blank");
    },
    handleCurrentQuestionChanged(question) {
      this.$emit("currentQuestionChanged", question);
    },
  },
};
</script>

<style lang="scss">
@import "@styles/abstracts/mixins";
@import "@styles/themes/sls-theme/tokens/colors";
@import "@styles/themes/sls-theme/tokens/spacing";
@import "@styles/themes/sls-theme/tokens/typo";

.search-resource-detail {
  .activity-list {
    margin-bottom: $spacing-05;
    padding-bottom: $spacing-05;
    border-bottom: 1px solid $ui-03;

    .activity-list-datatable {
      > .v-table {
        > .v-table__wrapper {
          border-bottom: 1px solid $ui-03;
          border-radius: 10px;
        }
      }
    }

    .v-table__wrapper {
      .v-data-table-header {
        .text-start {
          padding: $spacing-05;

          span {
            @include carbon--type-style("productive-heading-01");
          }
        }
      }

      table {
        > tbody {
          > tr {
            > td {
              &:nth-child(1) {
                .table-cell-wrapper {
                  // Intended specificity to override generic styling
                  &:not(.checkbox):not(.action) {
                    margin-right: 0;

                    .page-title-group {
                      > span {
                        overflow: hidden;
                      }
                    }
                  }
                }
              }

              .table-cell-wrapper {
                padding: $spacing-05;
                flex-flow: column wrap;
                align-items: center;

                &.simple-view {
                  .label-component,
                  .notes {
                    display: none;
                  }
                }

                &.has-select-action {
                  > .activity-header-wrapper {
                    .page-title-group {
                      max-width: calc(100% - 7rem);
                    }
                  }
                }

                > span {
                  max-width: unset;
                }

                > .activity-header-wrapper {
                  width: 100%;
                  display: flex;
                  align-items: center;

                  .page-title-group {
                    max-width: calc(100% - 5rem);
                  }
                }

                .notes {
                  @include carbon--type-style("body-short-01");
                  width: 100%;
                  margin-top: $spacing-03;
                  padding-left: $spacing-05;
                  color: $text-01;

                  > .rich-text-content {
                    padding-top: $spacing-03;
                    border-top: $divider-02;
                  }
                }
              }
            }
          }
        }
      }

      th {
        background-color: $ui-04;

        .all-checkbox {
          display: inline-flex;
          vertical-align: middle;

          + span {
            margin-left: $spacing-03;
          }
        }
      }
    }

    .table-cell-wrapper {
      background-color: $ui-02;
    }

    .bx--link {
      &:hover {
        color: $link-01;
        text-decoration: none;
      }
    }

    .page-title {
      @include carbon--type-style("panel-title-01", true);
      @include line-clamp(2);
      margin-right: $spacing-03;
      color: $text-02;
    }

    .activity-checkbox {
      .input-checkbox:not(.selected) {
        .sls-icon {
          [fill] {
            fill: $ui-01;
          }
        }
      }

      + span {
        margin-left: $spacing-03;
      }
    }

    .icon {
      display: flex;
      align-items: center;
      flex: 0 0 auto;
      position: relative;
      margin-right: $spacing-03;
    }

    .page-label {
      @include carbon--type-style("helper-text-01", true);
      position: absolute;
      top: 0.7rem;
      left: 50%;
      transform: translateX(-50%);
      color: $icon-01;
    }

    .view-ext {
      margin-left: auto;
      cursor: pointer;
      min-width: 16px;

      [stroke] {
        stroke: $interactive-01;
      }

      [fill] {
        fill: $interactive-01;
      }
    }
  }

  .bx--inline-notification {
    margin-top: 0;
  }

  .bx--form {
    &.linked-resources {
      border-top: none;
      margin-top: 0;
      padding-top: 0;
    }
  }

  .field-set {
    &.content-tag {
      .resource-tagging-view-item {
        background-color: $ui-background;
        border-radius: 10px;
      }
    }

    &.modified-by,
    &.created-by {
      .field-value {
        display: flex;
        align-items: center;
        flex-flow: row wrap;

        .date {
          margin-top: $spacing-02;
          flex: 0 0 100%;
        }
      }

      .creditor {
        width: auto;
        margin-right: $spacing-03;
      }
    }

    &:not(.used-in) {
      .label-component {
        span {
          @include carbon--type-style("label-01", true);
          color: $text-01;
        }
      }
    }
  }
}
</style>
